import React from 'react';
import {
  Box,
  Card,
  Typography,
  Avatar,
  Divider,
  useTheme,
  Chip,
  Tooltip,
} from '@ublend-npm/aulaui-core';
import { useSelector } from 'react-redux';
import TimeAgo from 'react-timeago';
import { postContentParser } from './postContentParser';
import {
  ChatBubbleOutlined,
  EmojiEmotionsOutlined,
  FilePresentOutlined,
} from '@mui/icons-material';
import {
  timeFormatter,
  getFullName,
  getUserInitials,
  getFeedLabel,
} from './recentUpdates.utils';
import SpaceDuration from '../common/SpaceDuration';

export interface RecentUpdateCardProps {
  item: any;
  onReadMore?: (item: any, scrollTo?: 'comments' | 'reactions') => void;
}

const RecentUpdateCard: React.FC<RecentUpdateCardProps> = ({
  item,
  onReadMore,
}) => {
  const theme = useTheme();

  const educatorChipColor = theme.palette.secondary.main;
  const studentChipColor = theme.palette.primary.light;

  const classRoomsMap = useSelector(
    (state: any) => state.data?.classRooms || {}
  );
  const classRooms = Object.values(classRoomsMap);
  const matchedClassRoom = classRooms.find(
    (room: any) =>
      room.objectId === item?.classRoom || room.id === item?.classRoom
  ) as any;
  const classRoomName = matchedClassRoom?.name;

  const { text, imagesCount, filesCount, integrationsCount, linksCount } =
    postContentParser(item?.content);
  const totalAttachments =
    imagesCount + filesCount + integrationsCount + linksCount;
  const hasAttachments = totalAttachments > 0;

  const fullName = getFullName(item?.user);
  const avatarUrl = item?.user?.avatar || '';
  const userInitials = getUserInitials(item?.user);
  const feedLabel = getFeedLabel(item?.feedType);
  const isEducatorPost = item?.postedAs === 'educator';

  // Text-only: 4-line clamp; with attachments: 2-line clamp
  const lineClamp = hasAttachments ? 2 : 4;

  // Approximate char-per-line heuristic to decide if "Read more" is needed.
  // ~55 chars per line is a safe estimate for the card width.
  const CHARS_PER_LINE = 55;
  const estimatedLines = text
    .split('\n')
    .reduce((acc: number, line: string) => {
      return acc + Math.ceil(Math.max(1, line.length) / CHARS_PER_LINE);
    }, 0);
  const isTextTruncated = estimatedLines > lineClamp;

  const reactionsCount =
    item?._reactionsCount ??
    (item?.reactions?.reduce(
      (sum: number, r: any) =>
        sum + (typeof r.count === 'number' ? r.count : 1),
      0
    ) ||
      0);
  const commentsCount =
    item?._commentsCount ??
    (item?.comments?.filter((c: any) => !c.parent).length || 0);

  const formattedDate = item?.createdAt
    ? new Date(item.createdAt).toLocaleDateString()
    : '';
  const accessibilityLabel = `${fullName} posted in ${
    classRoomName || feedLabel
  } on ${formattedDate}. ${text}. ${
    totalAttachments > 0
      ? `Has ${totalAttachments} attachment${totalAttachments > 1 ? 's' : ''}.`
      : ''
  } ${reactionsCount} reaction${
    reactionsCount !== 1 ? 's' : ''
  }, ${commentsCount} comment${commentsCount !== 1 ? 's' : ''}.`;

  const handleOpen = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    onReadMore?.(item);
  };

  const handleOpenComments = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    onReadMore?.(item, 'comments');
  };

  const handleOpenReactions = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    onReadMore?.(item, 'reactions');
  };

  const handleKeyOpen = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onReadMore?.(item);
    }
  };

  const handleKeyOpenComments = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onReadMore?.(item, 'comments');
    }
  };

  const handleKeyOpenReactions = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onReadMore?.(item, 'reactions');
    }
  };

  const readMoreLink = (
    <Typography
      component="span"
      role="button"
      tabIndex={0}
      onClick={handleOpen}
      onKeyDown={handleKeyOpen}
      aria-label={`Read more of ${fullName}'s update`}
      sx={{
        color: theme.palette.primary.main,
        cursor: 'pointer',
        fontWeight: 'medium',
        textDecoration: 'underline',
        display: 'inline',
        fontSize: 'inherit',
        lineHeight: 'inherit',
      }}
    >
      Read more
    </Typography>
  );

  return (
    <Card
      role="article"
      tabIndex={0}
      aria-label={accessibilityLabel}
      sx={{
        borderRadius: 6,
        padding: 2.5,
        boxShadow: 'none',
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.mode === 'dark' ? '#363636' : '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        height: '100%',
        boxSizing: 'border-box',
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: '2px',
        },
      }}
    >
      <Chip
        label={feedLabel}
        sx={{
          bgcolor: isEducatorPost ? educatorChipColor : studentChipColor,
          color: theme.palette.secondary.contrastText,
          cursor: 'auto',
        }}
      />

      <Box display="flex" alignItems="center" gap={1.5}>
        <Avatar
          src={avatarUrl}
          fallbackText={userInitials}
          sx={{ width: 40, height: 40, cursor: 'auto' }}
          aria-hidden="true"
        />
        <Box>
          <Typography variant="body1" color="textPrimary" fontWeight="medium">
            {fullName}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <TimeAgo date={item?.createdAt} formatter={timeFormatter} />
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
        <Box>
          <Typography
            variant="body1"
            color="textPrimary"
            sx={{
              whiteSpace: 'pre-line',
              lineHeight: 1.6,
              maxHeight: `${lineClamp * 1.6}em`,
              overflow: 'hidden',
            }}
          >
            {text}
          </Typography>

          {isTextTruncated && <Box mt={0.25}>{readMoreLink}</Box>}
        </Box>

        {hasAttachments && (
          <Box
            role="button"
            tabIndex={0}
            onClick={handleOpen}
            onKeyDown={handleKeyOpen}
            aria-label={`View ${totalAttachments} attachment${
              totalAttachments > 1 ? 's' : ''
            } in ${fullName}'s update`}
            display="flex"
            alignItems="center"
            gap={0.5}
            sx={{
              color: theme.palette.primary.main,
              cursor: 'pointer',
              borderRadius: 1,
              '&:hover': { opacity: 0.8 },
              '&:focus-visible': {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: '2px',
              },
            }}
            bgcolor={theme.palette.background.default}
            p={1}
          >
            <FilePresentOutlined
              fontSize="medium"
              color="inherit"
              aria-hidden="true"
            />
            <Typography
              variant="subtitle1"
              color="inherit"
              lineHeight={2.5}
              fontWeight="medium"
              sx={{ flex: 1 }}
            >
              {totalAttachments} attachment{totalAttachments > 1 ? 's' : ''}
            </Typography>
          </Box>
        )}

        {/* Spacer: pushes reactions/comments to the bottom of the body */}
        <Box sx={{ flex: 1 }} />

        <Box
          display="flex"
          alignItems="center"
          gap={3}
          sx={{ cursor: 'pointer' }}
        >
          <Box
            display="flex"
            role="button"
            tabIndex={0}
            data-tour="home-recent-updates-card-cta"
            aria-label={`${reactionsCount} reaction${
              reactionsCount !== 1 ? 's' : ''
            }. Open post`}
            alignItems="center"
            gap={1}
            onClick={handleOpenReactions}
            onKeyDown={handleKeyOpenReactions}
            className="stats-icon"
            sx={{
              color: theme.palette.text.secondary,
              cursor: 'pointer',
              borderRadius: 1,
              '&:hover .stats-icon': { opacity: 0.7 },
              '&:focus-visible': {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: '2px',
              },
            }}
          >
            <EmojiEmotionsOutlined
              fontSize="small"
              color="inherit"
              aria-hidden="true"
            />
            <Typography variant="body2" color="inherit" aria-hidden="true">
              {reactionsCount}
            </Typography>
          </Box>
          {feedLabel !== 'Announcements' && (
            <Box
              display="flex"
              role="button"
              tabIndex={0}
              data-tour="home-recent-updates-card-comments"
              aria-label={`${commentsCount} comment${
                commentsCount !== 1 ? 's' : ''
              }. Open post`}
              alignItems="center"
              gap={1}
              onClick={handleOpenComments}
              onKeyDown={handleKeyOpenComments}
              className="stats-icon"
              sx={{
                color: theme.palette.text.secondary,
                cursor: 'pointer',
                borderRadius: 1,
                '&:hover .stats-icon': { opacity: 0.7 },
                '&:focus-visible': {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: '2px',
                },
              }}
            >
              <ChatBubbleOutlined
                fontSize="small"
                color="inherit"
                aria-hidden="true"
              />
              <Typography variant="body2" color="inherit" aria-hidden="true">
                {commentsCount}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Divider sx={{ borderColor: theme.palette.divider }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <Typography noWrap variant="body2" color="textSecondary">
          <Tooltip title={classRoomName}>{classRoomName}</Tooltip>
        </Typography>
        <Typography variant="body2" color={theme.palette.text.secondary} noWrap>
          <SpaceDuration
            startDate={matchedClassRoom?.startDate}
            endDate={matchedClassRoom?.endDate}
            gap={8}
          />
        </Typography>
      </Box>
    </Card>
  );
};

export default RecentUpdateCard;
